import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

interface PublicSeoPage {
  titleKey: string;
  descriptionKey: string;
}

const SITE_ORIGIN = 'https://basilethiry.fr';
const SOCIAL_IMAGE = `${SITE_ORIGIN}/assets/images/BasileWebsite.webp`;

const PUBLIC_PAGES: Record<string, PublicSeoPage> = {
  '/': { titleKey: 'SEO_HOME_TITLE', descriptionKey: 'SEO_HOME_DESCRIPTION' },
  '/about': { titleKey: 'SEO_ABOUT_TITLE', descriptionKey: 'SEO_ABOUT_DESCRIPTION' },
  '/portfolio': { titleKey: 'SEO_PORTFOLIO_TITLE', descriptionKey: 'SEO_PORTFOLIO_DESCRIPTION' },
  '/school-career': { titleKey: 'SEO_CAREER_TITLE', descriptionKey: 'SEO_CAREER_DESCRIPTION' },
  '/contact': { titleKey: 'SEO_CONTACT_TITLE', descriptionKey: 'SEO_CONTACT_DESCRIPTION' },
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private readonly meta: Meta,
    private readonly title: Title,
    private readonly translate: TranslateService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  /** Synchronise les signaux SEO avec la route et la langue actuellement affichées. */
  update(path: string, fallbackTitleKey: string): void {
    const normalizedPath = this.normalizePath(path);
    const page = PUBLIC_PAGES[normalizedPath];
    if (!page) {
      this.setPrivatePage(fallbackTitleKey);
      return;
    }

    const pageTitle = this.translate.instant(page.titleKey);
    const description = this.translate.instant(page.descriptionKey);
    const canonicalUrl = normalizedPath === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${normalizedPath}`;

    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index,follow,max-image-preview:large' });
    this.meta.updateTag({ property: 'og:site_name', content: 'Basile Thiry' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:locale', content: this.translate.currentLang === 'en' ? 'en_US' : 'fr_FR' });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: SOCIAL_IMAGE });
    this.meta.updateTag({ property: 'og:image:alt', content: this.translate.instant('SEO_SOCIAL_IMAGE_ALT') });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: SOCIAL_IMAGE });
    this.setCanonical(canonicalUrl);
    this.setStructuredData(canonicalUrl);
  }

  private setPrivatePage(titleKey: string): void {
    this.title.setTitle(`${this.translate.instant(titleKey)} · Basile Thiry`);
    this.meta.updateTag({ name: 'robots', content: 'noindex,nofollow,noarchive' });
    this.meta.removeTag('name="description"');
    for (const property of ['og:site_name', 'og:type', 'og:locale', 'og:title', 'og:description', 'og:url', 'og:image', 'og:image:alt']) {
      this.meta.removeTag(`property="${property}"`);
    }
    for (const name of ['twitter:card', 'twitter:title', 'twitter:description', 'twitter:image']) {
      this.meta.removeTag(`name="${name}"`);
    }
    this.document.head.querySelector('link[rel="canonical"]')?.remove();
    this.document.getElementById('structured-data')?.remove();
  }

  private setCanonical(url: string): void {
    let canonical = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.rel = 'canonical';
      this.document.head.appendChild(canonical);
    }
    canonical.href = url;
  }

  private setStructuredData(url: string): void {
    let script = this.document.getElementById('structured-data') as HTMLScriptElement | null;
    if (!script) {
      script = this.document.createElement('script');
      script.id = 'structured-data';
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Basile Thiry',
      url: `${SITE_ORIGIN}/`,
      mainEntityOfPage: url,
      image: `${SITE_ORIGIN}/assets/images/Basile.webp`,
      jobTitle: this.translate.instant('SEO_JOB_TITLE'),
      sameAs: [
        'https://github.com/Riqoulouru',
        'https://gitlab.com/Riqou',
        'https://www.linkedin.com/in/basilethiry',
      ],
      knowsAbout: ['Java', 'Spring Boot', 'Angular', 'TypeScript', 'PostgreSQL'],
    });
  }

  private normalizePath(path: string): string {
    const withoutQuery = path.split(/[?#]/)[0] || '/';
    return withoutQuery === '/' ? '/' : withoutQuery.replace(/\/+$/, '');
  }
}
